import { createFileRoute } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { FeatureGate } from "@/components/FeatureGate";
import { useEffect, useMemo, useState } from "react";
import { es as esLocale } from "date-fns/locale";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTrialLimits } from "@/hooks/useTrialLimits";
import { TrialLimitBanner } from "@/components/TrialLimitBanner";
import {
  publishToPlatform,
  PLATFORM_LABELS,
  PLATFORM_FORMATS,
  type PublishPlatform,
  type PublishStatus,
} from "@/lib/post-publisher";

export const Route = createFileRoute("/_app/content")({
  component: () => (
    <FeatureGate feature="content">
      <ContentCalendarPage />
    </FeatureGate>
  ),
  head: () => ({
    meta: [
      { title: "Content Calendar · AdKavi" },
      { name: "description", content: "Plan and schedule social media posts across platforms." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Platform = "instagram" | "facebook" | "tiktok" | "linkedin" | "twitter" | "youtube";
type Kind = "post" | "carousel" | "reel" | "story" | "video";
type Status = "draft" | "scheduled" | "published" | "failed";

type ScheduledPost = {
  id: string;
  title: string;
  caption: string | null;
  platform: Platform;
  kind: Kind;
  status: Status;
  scheduled_at: string;
};

const PLATFORMS: { value: Platform; label: string; color: string }[] = [
  { value: "instagram", label: "Instagram", color: "bg-pink-500/15 text-pink-400 border-pink-500/30" },
  { value: "facebook", label: "Facebook", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  { value: "tiktok", label: "TikTok", color: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  { value: "linkedin", label: "LinkedIn", color: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  { value: "twitter", label: "X / Twitter", color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  { value: "youtube", label: "YouTube", color: "bg-red-500/15 text-red-400 border-red-500/30" },
];

const KINDS: { value: Kind; label: string }[] = [
  { value: "post", label: "Post" },
  { value: "carousel", label: "Carousel" },
  { value: "reel", label: "Reel" },
  { value: "story", label: "Story" },
  { value: "video", label: "Video" },
];

const STATUS_STYLES: Record<Status, string> = {
  draft: "bg-muted text-muted-foreground border-border",
  scheduled: "bg-primary/15 text-primary border-primary/30",
  published: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
};

function platformMeta(p: Platform) {
  return PLATFORMS.find((x) => x.value === p) ?? PLATFORMS[0];
}

function ContentCalendarPage() {
  const { t, lang } = useI18n();
  const dateLocale = lang === "es" ? esLocale : undefined;
  const { user } = useAuth();
  const { isLocked, activeClientId } = useTrialLimits();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<{ post?: ScheduledPost; date?: Date } | null>(null);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart, gridEnd]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("scheduled_posts")
      .select("id,title,caption,platform,kind,status,scheduled_at")
      .gte("scheduled_at", gridStart.toISOString())
      .lte("scheduled_at", gridEnd.toISOString())
      .order("scheduled_at", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) toast.error(t("content.toast.loadFailed"), { description: error.message });
        else setPosts((data ?? []) as ScheduledPost[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, gridStart.getTime(), gridEnd.getTime()]);

  const postsByDay = useMemo(() => {
    const map = new Map<string, ScheduledPost[]>();
    for (const p of posts) {
      const key = format(parseISO(p.scheduled_at), "yyyy-MM-dd");
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [posts]);

  const counts = useMemo(() => {
    const inMonth = posts.filter((p) => isSameMonth(parseISO(p.scheduled_at), cursor));
    return {
      total: inMonth.length,
      scheduled: inMonth.filter((p) => p.status === "scheduled").length,
      published: inMonth.filter((p) => p.status === "published").length,
      drafts: inMonth.filter((p) => p.status === "draft").length,
    };
  }, [posts, cursor]);

  const handleSaved = (saved: ScheduledPost, isNew: boolean) => {
    setPosts((prev) =>
      isNew ? [...prev, saved] : prev.map((p) => (p.id === saved.id ? saved : p))
    );
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("scheduled_posts").delete().eq("id", id);
    if (error) {
      toast.error(t("content.toast.deleteFailed"), { description: error.message });
      return;
    }
    setPosts((prev) => prev.filter((p) => p.id !== id));
    setEditing(null);
    toast.success(t("content.toast.deleted"));
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("content.title")}</span>
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t("content.subtitle")}
          </p>
        </div>
        <Button
          onClick={() => setEditing({ date: new Date() })}
          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white self-start md:self-auto"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {t("content.createPost")}
        </Button>
      </div>

      <TrialLimitBanner kind="post" />
      <TrialLimitBanner kind="carousel" onlyWhenLocked />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label={t("content.stat.thisMonth")} value={counts.total} />
        <Stat label={t("content.stat.scheduled")} value={counts.scheduled} accent="text-primary" />
        <Stat label={t("content.stat.published")} value={counts.published} accent="text-emerald-400" />
        <Stat label={t("content.stat.drafts")} value={counts.drafts} accent="text-muted-foreground" />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCursor((d) => addMonths(d, -1))}
              aria-label={t("content.cal.prevMonth")}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-lg font-semibold tabular-nums min-w-[180px] text-center">
              {format(cursor, "MMMM yyyy", { locale: dateLocale })}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCursor((d) => addMonths(d, 1))}
              aria-label={t("content.cal.nextMonth")}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setCursor(startOfMonth(new Date()))}
          >
            {t("content.cal.today")}
          </Button>
        </div>

        <div className="grid grid-cols-7 border-b border-white/[0.08] bg-muted/20 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {(["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const).map((d) => (
            <div key={d} className="px-2 py-2.5 text-center">
              {t(`content.cal.${d}`)}
            </div>
          ))}
        </div>

        <div className="relative grid grid-cols-7 auto-rows-[minmax(145px,1fr)]">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/40 backdrop-blur-sm">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && posts.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
              <div className="text-center px-4">
                <CalendarIcon className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground/60 max-w-xs leading-relaxed">
                  {t("content.empty")}
                </p>
              </div>
            </div>
          )}
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayPosts = postsByDay.get(key) ?? [];
            const inMonth = isSameMonth(day, cursor);
            const today = isToday(day);
            return (
              <button
                key={key}
                type="button"
                onClick={() => setEditing({ date: day })}
                className={cn(
                  "group text-left p-2.5 border-r border-b border-white/[0.08] last:border-r-0 transition-colors hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-inset",
                  !inMonth && "bg-black/20"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={cn(
                      "inline-flex items-center justify-center text-xs font-semibold w-6 h-6 rounded-full",
                      today
                        ? "bg-orange-500 text-white"
                        : inMonth
                        ? "text-foreground"
                        : "text-muted-foreground/40"
                    )}
                  >
                    {format(day, "d")}
                  </span>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 hover:bg-primary/30">
                    <Plus className="w-3 h-3 text-primary" />
                  </span>
                </div>
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map((p) => {
                    const meta = platformMeta(p.platform);
                    return (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing({ post: p });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditing({ post: p });
                          }
                        }}
                        className={cn(
                          "text-[11px] leading-tight px-1.5 py-1 rounded border truncate cursor-pointer hover:opacity-80",
                          meta.color
                        )}
                      >
                        <span className="font-medium tabular-nums mr-1">
                          {format(parseISO(p.scheduled_at), "HH:mm")}
                        </span>
                        {p.title}
                      </div>
                    );
                  })}
                  {dayPosts.length > 3 && (
                    <div className="text-[11px] text-muted-foreground px-1.5">
                      {t("content.cal.more").replace("{n}", String(dayPosts.length - 3))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <PostDialog
        open={!!editing}
        initialDate={editing?.date}
        post={editing?.post}
        userId={user?.id}
        activeClientId={activeClientId}
        isLocked={isLocked}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
        onDelete={handleDelete}
      />
    </section>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("text-2xl font-bold tabular-nums mt-0.5", accent)}>{value}</div>
    </div>
  );
}

function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PostDialog({
  open,
  initialDate,
  post,
  userId,
  activeClientId,
  isLocked,
  onClose,
  onSaved,
  onDelete,
}: {
  open: boolean;
  initialDate?: Date;
  post?: ScheduledPost;
  userId?: string;
  activeClientId: string | null;
  isLocked: (kind: "campaign" | "post" | "carousel" | "ai_video") => boolean;
  onClose: () => void;
  onSaved: (p: ScheduledPost, isNew: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [platform, setPlatform] = useState<Platform>("instagram");
  const [kind, setKind] = useState<Kind>("post");
  const [status, setStatus] = useState<Status>("scheduled");
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);

  // AI caption generation state
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ text: string; hashtags: string[] }>>([]);

  // Multi-platform publishing state
  const [pubs, setPubs] = useState<PublicationDraft[]>([]);
  const [loadingPubs, setLoadingPubs] = useState(false);

  const PLATFORM_TO_API: Record<Platform, string> = {
    instagram: "meta",
    facebook: "meta",
    tiktok: "tiktok",
    linkedin: "linkedin",
    twitter: "meta",
    youtube: "youtube",
  };

  const generateCaption = async () => {
    if (!title.trim()) return;
    setGenerating(true);
    setSuggestions([]);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ad-copy", {
        body: {
          product: title.trim(),
          platform: PLATFORM_TO_API[platform],
          goal: "awareness",
          tone: "friendly",
          count: 3,
        },
      });
      if (error || !data?.variations?.length) throw new Error(error?.message ?? "No results");
      setSuggestions(
        (data.variations as Array<{ primary_text: string; hashtags?: string[] }>).map((v) => ({
          text: v.primary_text,
          hashtags: v.hashtags ?? [],
        })),
      );
    } catch {
      toast.error(t("content.generate.failed"));
    } finally {
      setGenerating(false);
    }
  };

  const useSuggestion = (s: { text: string; hashtags: string[] }) => {
    const needsHashtags = platform === "instagram" || platform === "tiktok";
    const full = needsHashtags && s.hashtags.length > 0
      ? `${s.text}\n\n${s.hashtags.map((h) => `#${h}`).join(" ")}`
      : s.text;
    setCaption(full);
    setSuggestions([]);
  };

  useEffect(() => {
    if (!open) return;
    if (post) {
      setTitle(post.title);
      setCaption(post.caption ?? "");
      setPlatform(post.platform);
      setKind(post.kind);
      setStatus(post.status);
      setWhen(toLocalInput(parseISO(post.scheduled_at)));
    } else {
      const base = initialDate ?? new Date();
      const d = new Date(base);
      if (!initialDate || isSameDay(initialDate, new Date())) {
        // keep current time
      } else {
        d.setHours(9, 0, 0, 0);
      }
      setTitle("");
      setCaption("");
      setPlatform("instagram");
      setKind("post");
      setStatus("scheduled");
      setWhen(toLocalInput(d));
      setPubs([]);
      setSuggestions([]);
    }
  }, [open, post, initialDate]);

  // Load existing publications for this post
  useEffect(() => {
    if (!open || !post) {
      if (!post) setPubs([]);
      return;
    }
    let cancelled = false;
    setLoadingPubs(true);
    supabase
      .from("post_publications")
      .select("id,platform,caption,format,scheduled_at,status,error_message")
      .eq("post_id", post.id)
      .then(({ data }) => {
        if (cancelled) return;
        setPubs(
          ((data ?? []) as PostPublicationRow[]).map((row) => ({
            id: row.id,
            platform: row.platform as PublishPlatform,
            caption: row.caption ?? "",
            format: row.format,
            scheduled_at: toLocalInput(parseISO(row.scheduled_at)),
            status: row.status as PublishStatus,
            error_message: row.error_message,
          })),
        );
        setLoadingPubs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, post]);

  const togglePlatform = (p: PublishPlatform) => {
    setPubs((prev) => {
      const found = prev.find((x) => x.platform === p);
      if (found) return prev.filter((x) => x.platform !== p);
      return [
        ...prev,
        {
          platform: p,
          caption,
          format: PLATFORM_FORMATS[p][0]?.value ?? "post",
          scheduled_at: when,
          status: "scheduled",
          error_message: null,
        },
      ];
    });
  };

  const updatePub = (p: PublishPlatform, patch: Partial<PublicationDraft>) => {
    setPubs((prev) => prev.map((x) => (x.platform === p ? { ...x, ...patch } : x)));
  };

  const handleSave = async () => {
    if (!userId) return;
    if (!title.trim()) {
      toast.error(t("content.toast.addTitle"));
      return;
    }
    if (!when) {
      toast.error(t("content.toast.pickDate"));
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      caption: caption.trim() || null,
      platform,
      kind,
      status,
      scheduled_at: new Date(when).toISOString(),
    };
    let savedPost: ScheduledPost | null = null;
    let isNew = false;
    if (post) {
      const { data, error } = await supabase
        .from("scheduled_posts")
        .update(payload)
        .eq("id", post.id)
        .select("id,title,caption,platform,kind,status,scheduled_at")
        .single();
      if (error || !data) {
        setSaving(false);
        toast.error(t("content.toast.saveFailed"), { description: error?.message });
        return;
      }
      savedPost = data as ScheduledPost;
    } else {
      if ((kind === "post" || kind === "carousel") && isLocked(kind)) {
        setSaving(false);
        toast.error(t("content.toast.trialLimit"), {
          description: `Only 1 ${kind} per client during the 14-day trial. Switch client or wait until billing starts.`,
        });
        return;
      }
      const { data, error } = await supabase
        .from("scheduled_posts")
        .insert({ ...payload, user_id: userId, client_id: activeClientId ?? null })
        .select("id,title,caption,platform,kind,status,scheduled_at")
        .single();
      if (error || !data) {
        setSaving(false);
        toast.error(t("content.toast.scheduleFailed"), { description: error?.message });
        return;
      }
      savedPost = data as ScheduledPost;
      isNew = true;
    }

    // Sync per-platform publications
    if (savedPost) {
      try {
        await syncPublications(savedPost.id, userId, activeClientId, pubs);
      } catch (err) {
        console.error("Publication sync failed", err);
        toast.error(t("publish.toast.syncFailed"));
      }
    }

    setSaving(false);
    onSaved(savedPost!, isNew);
    toast.success(isNew ? t("content.toast.scheduled") : t("content.toast.updated"));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{post ? t("content.dialog.editTitle") : t("content.dialog.scheduleTitle")}</DialogTitle>
          <DialogDescription>
            {post ? t("content.dialog.editDesc") : t("content.dialog.scheduleDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="post-title">{t("content.field.title")}</Label>
            <Input
              id="post-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("content.field.titlePlaceholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("content.field.platform")}</Label>
              <Select value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("content.field.type")}</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {t(`content.kind.${k.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="post-when">{t("content.field.when")}</Label>
              <Input
                id="post-when"
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("content.field.status")}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("content.status.draft")}</SelectItem>
                  <SelectItem value="scheduled">{t("content.status.scheduled")}</SelectItem>
                  <SelectItem value="published">{t("content.status.published")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="post-caption">{t("content.field.caption")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={generateCaption}
                disabled={generating || !title.trim()}
                className="h-7 px-2 text-xs gap-1.5 text-primary hover:text-primary"
              >
                {generating
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
                {generating ? t("content.generate.generating") : t("content.generate.button")}
              </Button>
            </div>
            <Textarea
              id="post-caption"
              value={caption}
              onChange={(e) => { setCaption(e.target.value); if (suggestions.length) setSuggestions([]); }}
              placeholder={t("content.field.captionPlaceholder")}
              rows={3}
            />
            {suggestions.length > 0 && (
              <div className="space-y-2 pt-1">
                <p className="text-xs text-muted-foreground">{t("content.generate.pick")}</p>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => useSuggestion(s)}
                    className="w-full text-left rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 px-3 py-2.5 text-xs transition-colors"
                  >
                    <p className="leading-relaxed line-clamp-3">{s.text}</p>
                    {s.hashtags.length > 0 && (
                      <p className="mt-1.5 text-primary/60 truncate">
                        {s.hashtags.slice(0, 6).map((h) => `#${h}`).join(" ")}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Multi-platform publishing */}
          <div className="space-y-3 rounded-lg border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("publish.title")}</div>
              <p className="text-xs text-muted-foreground">{t("publish.desc")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(PLATFORM_LABELS) as PublishPlatform[]).map((p) => {
                const active = pubs.some((x) => x.platform === p);
                return (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    onClick={() => togglePlatform(p)}
                    className="rounded-full"
                  >
                    {PLATFORM_LABELS[p]}
                  </Button>
                );
              })}
            </div>

            {loadingPubs ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> {t("content.pub.loading")}
              </div>
            ) : pubs.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t("publish.empty")}</p>
            ) : (
              <div className="space-y-3">
                {pubs.map((pub) => (
                  <PublicationRow
                    key={pub.platform}
                    pub={pub}
                    onUpdate={(patch) => updatePub(pub.platform, patch)}
                  />
                ))}
              </div>
            )}
          </div>

          {post && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className={cn("border", STATUS_STYLES[post.status])}>
                {t(`content.status.${post.status}`)}
              </Badge>
              <span>{t("content.badge.createdPost")}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {post ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(post.id)}
              disabled={saving}
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> {t("content.btn.delete")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              {t("content.btn.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {post ? t("content.btn.save") : t("content.btn.schedule")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type PublicationDraft = {
  id?: string;
  platform: PublishPlatform;
  caption: string;
  format: string;
  scheduled_at: string; // datetime-local
  status: PublishStatus;
  error_message: string | null;
};

type PostPublicationRow = {
  id: string;
  platform: string;
  caption: string | null;
  format: string;
  scheduled_at: string;
  status: string;
  error_message: string | null;
};

const PUB_STATUS_STYLES: Record<PublishStatus, string> = {
  scheduled: "bg-primary/15 text-primary border-primary/30",
  published: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
};

function PublicationRow({
  pub,
  onUpdate,
}: {
  pub: PublicationDraft;
  onUpdate: (patch: Partial<PublicationDraft>) => void;
}) {
  const { t } = useI18n();
  const formats = PLATFORM_FORMATS[pub.platform];
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{PLATFORM_LABELS[pub.platform]}</div>
        <Badge variant="outline" className={cn("border text-[10px]", PUB_STATUS_STYLES[pub.status])}>
          {t(`publish.status.${pub.status}`)}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">{t("publish.field.format")}</Label>
          <Select value={pub.format} onValueChange={(v) => onUpdate({ format: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formats.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("publish.field.when")}</Label>
          <Input
            type="datetime-local"
            value={pub.scheduled_at}
            onChange={(e) => onUpdate({ scheduled_at: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("publish.field.caption")}</Label>
        <Textarea
          value={pub.caption}
          onChange={(e) => onUpdate({ caption: e.target.value })}
          placeholder={t("publish.field.captionPlaceholder")}
          rows={2}
          className="text-xs"
        />
      </div>
      {pub.status === "failed" && pub.error_message && (
        <p className="text-[11px] text-destructive">{pub.error_message}</p>
      )}
    </div>
  );
}

async function syncPublications(
  postId: string,
  userId: string,
  clientId: string | null,
  drafts: PublicationDraft[],
) {
  // Get existing rows
  const { data: existing } = await supabase
    .from("post_publications")
    .select("id,platform")
    .eq("post_id", postId);
  const existingByPlatform = new Map(
    ((existing ?? []) as { id: string; platform: string }[]).map((r) => [r.platform, r.id]),
  );
  const draftPlatforms = new Set(drafts.map((d) => d.platform));

  // Delete removed
  const toDelete = (existing ?? [])
    .filter((r) => !draftPlatforms.has(r.platform as PublishPlatform))
    .map((r) => r.id);
  if (toDelete.length > 0) {
    await supabase.from("post_publications").delete().in("id", toDelete);
  }

  // Upsert each draft, then call publishToPlatform
  for (const draft of drafts) {
    const scheduledIso = draft.scheduled_at
      ? new Date(draft.scheduled_at).toISOString()
      : new Date().toISOString();

    // First call publisher (currently a mock that returns "scheduled")
    const result = await publishToPlatform({
      platform: draft.platform,
      caption: draft.caption || null,
      format: draft.format,
      scheduledAt: scheduledIso,
    });

    const row = {
      post_id: postId,
      user_id: userId,
      client_id: clientId,
      platform: draft.platform,
      caption: draft.caption || null,
      format: draft.format,
      scheduled_at: scheduledIso,
      status: result.status,
      provider_post_id: result.providerPostId ?? null,
      published_at: result.publishedAt ?? null,
      error_message: result.errorMessage ?? null,
    };
    const existingId = existingByPlatform.get(draft.platform);
    if (existingId) {
      await supabase.from("post_publications").update(row).eq("id", existingId);
    } else {
      await supabase.from("post_publications").insert(row);
    }
  }
}

